import type { BudgetResult, CableTier, NodeKind } from './api'

export const NODE_KIND_LABEL: Record<NodeKind, string> = {
  pop: 'POP / Central office',
  closure: 'Splice closure',
  lcp: 'LCP (primary splitter)',
  nap: 'NAP (subscriber terminal)',
  pole: 'Pole',
  handhole: 'Handhole',
}

export const NODE_KIND_SHORT: Record<NodeKind, string> = { pop: 'POP', closure: 'Closure', lcp: 'LCP', nap: 'NAP', pole: 'Pole', handhole: 'Handhole' }

/** Map colours. Tier colours are distinguishable for common colour-vision deficiencies and also differ in width. */
export const TIER_COLOR: Record<CableTier, string> = {
  backbone: '#7c3aed',
  feeder: '#ea580c',
  distribution: '#2563eb',
  drop: '#0f9d58',
}
export const TIER_WIDTH: Record<CableTier, number> = { backbone: 6, feeder: 5, distribution: 3.5, drop: 2 }
export const TIER_LABEL: Record<CableTier, string> = { backbone: 'Backbone', feeder: 'Feeder', distribution: 'Distribution', drop: 'Drop' }

export const NODE_COLOR: Record<NodeKind, string> = {
  pop: '#111827',
  closure: '#be185d',
  lcp: '#0e7490',
  nap: '#15803d',
  pole: '#6b7280',
  handhole: '#6b7280',
}

export const fmtM = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`)
export const fmtDb = (db: number) => `${db.toFixed(2)} dB`
export const fmtDbm = (dbm: number) => `${dbm.toFixed(2)} dBm`
/** Percent that never rounds a shortfall up to 100 % or a non-zero share down to 0 %. */
export function fmtPct(n: number, d: number): string {
  if (d === 0) return '—'
  const p = (n / d) * 100
  if (n !== d && p > 99.5) return `${Math.min(p, 99.9).toFixed(1)}%`
  if (n > 0 && p < 0.5) return '<1%'
  return `${Math.round(p)}%`
}

export function fmtAge(iso?: string, now = Date.now()): string {
  if (!iso) return 'no reading'
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000))
  if (s < 90) return `${s}s ago`
  if (s < 5400) return `${Math.round(s / 60)} min ago`
  if (s < 172800) return `${Math.round(s / 3600)} h ago`
  return `${Math.round(s / 86400)} d ago`
}

export type Tone = 'ok' | 'warn' | 'danger' | 'neutral' | 'accent'

/** One place that turns a budget into words and a tone; status never relies on colour alone. */
export function budgetVerdict(b: BudgetResult | null): { tone: Tone; label: string; hint: string } {
  if (!b) return { tone: 'danger', label: 'Not traceable', hint: 'The path does not reach an OLT port. A splice is missing or the fibre is open.' }
  if (b.deviates) return { tone: 'danger', label: `${Math.abs(b.deviationDb!).toFixed(1)} dB below design`, hint: 'Clean and re-seat the drop connectors, then check the NAP splice and drop for bends.' }
  switch (b.status) {
    case 'fail':
      return { tone: 'danger', label: 'Budget exceeded', hint: 'Designed loss is higher than the optics allow.' }
    case 'overload':
      return { tone: 'warn', label: 'Receiver overload', hint: 'Expected power is too high; fit an attenuator at the ONU.' }
    case 'low-margin':
      return { tone: 'warn', label: `Low margin (${b.marginDb.toFixed(1)} dB)`, hint: 'Less than 3 dB headroom for repairs and ageing.' }
    case 'ok':
      return { tone: 'ok', label: b.measuredRxDbm === undefined ? 'Design OK · no reading' : 'Within design', hint: '' }
  }
}
